// src/pages/admin/orders/AdminOrdersListPage.tsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FullScreenLoader } from "components/ui/Loader";
import { getErrorMessage } from "utils/errors";
import { formatMoney } from "utils/money";
import { adminListOrders, type AdminOrderOut } from "api/adminOrders";

type LineGeom = {
  left: number;
  titleTop: number;
  titleBottom: number;
  containerTop: number;
  containerBottom: number;
};

const GOLD = "#f3dfae";

const PAGE_SIZE = 20;

const AdminOrdersListPage: React.FC = () => {
  const [items, setItems] = useState<AdminOrderOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Title/container vertical line geometry (CookiesPage logic)
  const pageRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [geom, setGeom] = useState<LineGeom | null>(null);

  const load = async (p: number) => {
    setErr("");
    setLoading(true);
    try {
      const res = await adminListOrders(p, PAGE_SIZE);
      setItems(res.items || []);
      setTotal(res.total ?? 0);
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to load orders"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useLayoutEffect(() => {
    const page = pageRef.current;
    const title = titleRef.current;
    const container = containerRef.current;
    if (!page || !title || !container) return;

    const compute = () => {
      const p = page.getBoundingClientRect();
      const t = title.getBoundingClientRect();
      const c = container.getBoundingClientRect();

      const left = Math.round(t.left + t.width / 2 - p.left);

      setGeom({
        left: Math.max(0, left),
        titleTop: Math.max(0, Math.round(t.top - p.top)),
        titleBottom: Math.max(0, Math.round(t.bottom - p.top)),
        containerTop: Math.max(0, Math.round(c.top - p.top)),
        containerBottom: Math.max(0, Math.round(c.bottom - p.top))
      });
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(page);
    ro.observe(title);
    ro.observe(container);

    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [items.length, loading, err]);

  if (loading) return <FullScreenLoader label="Loading…" />;

  const TITLE_GAP_PX = 14;
  const CONTAINER_TOP_MARGIN_CLASS = "mt-16 sm:mt-20";

  return (
    <main ref={pageRef} className="relative min-h-screen px-4 py-12 sm:py-16">
      {/* Vertical gold line segments (with gaps for title + container) */}
      {geom && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: 0,
              bottom: `calc(100% - ${Math.max(0, geom.titleTop - TITLE_GAP_PX)}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.titleBottom + TITLE_GAP_PX,
              bottom: `calc(100% - ${geom.containerTop}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.containerBottom,
              bottom: 0
            }}
          />
        </>
      )}

      <div className="relative z-10">
        {/* Title */}
        <header className="text-center">
          <h1
            ref={titleRef}
            className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#ceddf2]"
          >
            Admin · Orders
          </h1>
        </header>

        {/* Main container (FULL WIDTH) */}
        <section className={[CONTAINER_TOP_MARGIN_CLASS, "flex justify-center"].join(" ")}>
          <div
            ref={containerRef}
            className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 sm:px-8"
          >
            <div className="flex items-start justify-between gap-4">
              <Link
                to="/admin"
                className="font-ui text-xs uppercase tracking-wide text-slate-500 hover:opacity-80"
              >
                ← Back to Admin Home
              </Link>

              <button
                type="button"
                onClick={() => load(page)}
                className={[
                  "inline-flex items-center justify-center rounded-full",
                  "border border-slate-200 bg-white",
                  "px-4 py-2",
                  "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                  "hover:bg-slate-50 transition-colors font-ui font-bold"
                ].join(" ")}
              >
                Refresh
              </button>
            </div>

            {err && (
              <div className="mt-6 border border-red-300 bg-red-50 text-red-700 p-3 rounded">
                {err}
              </div>
            )}

            <div className="mt-8">
              {items.length === 0 ? (
                <div className="font-body text-slate-600">No orders found.</div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-slate-700 font-ui text-xs uppercase tracking-wide">
                        <th className="text-left p-3">ID</th>
                        <th className="text-left p-3">Created</th>
                        <th className="text-left p-3">User</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Package</th>
                        <th className="text-left p-3">Total</th>
                        <th className="text-left p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((o) => {
                        const created = o.created_date
                          ? new Date(o.created_date).toLocaleString()
                          : "—";
                        const status = (o.status || "—").toString().toLowerCase();

                        const userLabel =
                          o.user_label ?? o.user_display_name ?? o.user_email ?? "—";

                        return (
                          <tr key={o.id} className="border-t border-slate-200">
                            <td className="p-3 font-body text-slate-900">#{o.id}</td>
                            <td className="p-3 font-body text-slate-700">{created}</td>
                            <td className="p-3 font-body text-slate-700 break-words">
                              {userLabel}
                            </td>
                            <td className="p-3">
                              <span
                                className={[
                                  "inline-flex items-center rounded-full px-3 py-1",
                                  "font-ui text-xs uppercase tracking-wide border",
                                  status === "paid" || status === "fulfilled" || status === "complete"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : status === "cancelled" || status === "refunded"
                                    ? "bg-red-50 text-red-700 border-red-200"
                                    : "bg-amber-50 text-amber-800 border-amber-200"
                                ].join(" ")}
                              >
                                {o.status}
                              </span>
                            </td>
                            <td className="p-3 font-body text-slate-700">
                              {o.package_name || "—"}
                            </td>
                            <td className="p-3 font-body text-slate-900">
                              {formatMoney(o.total_cents || 0, o.currency || "USD")}
                            </td>
                            <td className="p-3">
                              <Link
                                to={`/admin/orders/${o.id}`}
                                className={[
                                  "inline-flex items-center justify-center rounded-full",
                                  "bg-[#f3dfae] px-4 py-2",
                                  "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                                  "hover:opacity-90 transition-opacity font-ui font-bold"
                                ].join(" ")}
                              >
                                View / update
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination footer */}
            <div className="mt-6 border-t border-slate-200 pt-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="font-ui text-xs uppercase tracking-wide text-slate-500">
                {total > 0
                  ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total} orders`
                  : "Tip: click an order to manage status and fulfillment."}
              </div>
              {total > PAGE_SIZE && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className={[
                      "inline-flex items-center justify-center rounded-full",
                      "border border-slate-200 bg-white",
                      "px-4 py-2",
                      "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                      "font-ui font-bold transition-colors",
                      page <= 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-50"
                    ].join(" ")}
                  >
                    ← Prev
                  </button>
                  <span className="font-ui text-xs text-slate-600">
                    Page {page} of {Math.ceil(total / PAGE_SIZE)}
                  </span>
                  <button
                    type="button"
                    disabled={page >= Math.ceil(total / PAGE_SIZE)}
                    onClick={() => setPage((p) => p + 1)}
                    className={[
                      "inline-flex items-center justify-center rounded-full",
                      "border border-slate-200 bg-white",
                      "px-4 py-2",
                      "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                      "font-ui font-bold transition-colors",
                      page >= Math.ceil(total / PAGE_SIZE) ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-50"
                    ].join(" ")}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default AdminOrdersListPage;
